/**
 * The outside scripts the site loads, and how to tell each one is really there.
 *
 * Shared by the browser probe (utils/script-health.ts), the track schema and the
 * /dash/errors "Blocked scripts" tab, so a vendor is named the same in all three.
 *
 * `ready` reads the global the real script defines. It catches the case a load
 * event cannot: uBlock Origin and Brave swap some scripts (Google Analytics,
 * AdSense, GTM) for an empty stand-in that loads fine and does nothing.
 */

export const SCRIPT_HEALTH_EVENT = 'script_health';

type Globals = Record<string, unknown>;

/** `a.b.c` on the window, or undefined. */
function dig(w: Globals, path: string): unknown {
  return path.split('.').reduce<unknown>(
    (at, key) => (at && typeof at === 'object' ? (at as Globals)[key] : undefined),
    w,
  );
}

const isFunction = (path: string) => (w: Globals) => typeof dig(w, path) === 'function';
const isPresent = (path: string) => (w: Globals) => dig(w, path) != null;

export interface WatchedScript {
  id: string;
  label: string;
  /** Matched against the script's full URL. */
  match: RegExp;
  /** True once the real script has run. Left out where no global proves it. */
  ready?: (w: Globals) => boolean;
}

/**
 * Only what the site loads today (data/js/embeds.ts, CompositeAppProvider,
 * browse-page, checkout), plus the hosts those load in turn. The list gives a
 * script a friendly label and a `ready` check; a script not on it is still
 * counted, under its host (scriptIdFor).
 */
export const WATCHED_SCRIPTS: readonly WatchedScript[] = [
  { id: 'stripe', label: 'Stripe checkout', match: /\/\/js\.stripe\.com\//, ready: isFunction('Stripe') },
  {
    id: 'recaptcha',
    label: 'reCAPTCHA',
    match: /\/\/www\.(google\.com|recaptcha\.net)\/recaptcha\//,
    ready: (w) => isFunction('grecaptcha.enterprise.execute')(w) || isFunction('grecaptcha.execute')(w),
  },
  { id: 'google_maps', label: 'Google address lookup', match: /\/\/maps\.googleapis\.com\//, ready: isPresent('google.maps') },
  { id: 'facebook_sdk', label: 'Facebook login', match: /\/\/connect\.facebook\.net\/[^/]+\/sdk\.js/, ready: isFunction('FB.init') },
  { id: 'facebook_pixel', label: 'Facebook pixel', match: /\/\/connect\.facebook\.net\/[^/]+\/fbevents\.js/ },
  { id: 'google_analytics', label: 'Google Analytics', match: /\/\/www\.googletagmanager\.com\//, ready: isPresent('google_tag_manager') },
  { id: 'adsense', label: 'AdSense', match: /\/\/pagead2\.googlesyndication\.com\// },
  { id: 'sellwild_widget', label: 'Sellwild ads and video', match: /\/\/widget\.sellwild\.com\// },
  { id: 'bidstream', label: 'Bidstream ads', match: /\/\/ads\.bidstreamserver\.com\// },
  { id: 'onetrust', label: 'Cookie banner', match: /\/\/cdn\.cookielaw\.org\// },
  // Named 2026-10-31 from the hosts the tab listed by name only.
  { id: 'google_ad_manager', label: 'Google Ad Manager (GPT)', match: /\/\/securepubads\.g\.doubleclick\.net\// },
  { id: 'funding_choices', label: 'Google Funding Choices consent', match: /\/\/fundingchoicesmessages\.google\.com\// },
  { id: 'google_ima', label: 'Google IMA video ads SDK', match: /\/\/imasdk\.googleapis\.com\// },
  { id: 'gstatic', label: 'Google static files (gstatic)', match: /\/\/www\.gstatic\.com\// },
  { id: 'ad_traffic_quality', label: 'Google ad traffic quality', match: /\/\/[a-z0-9-]+\.adtrafficquality\.google\// },
  // After the SDK and the pixel, so those two keep their own rows.
  { id: 'facebook_connect', label: 'Facebook connect (other)', match: /\/\/connect\.facebook\.net\// },
  { id: 'sellwild_cache', label: 'Sellwild listing cache', match: /\/\/cache\.sellwild\.com\// },
  { id: 'own_code', label: 'Our own code', match: /\/_next\/static\// },
];

/**
 * Ours, so a failure is the network or a deploy, not a blocker. Left out when
 * a session is sorted into a blocker type (utils/clickhouse/blocker-type-sql.ts).
 */
export const FIRST_PARTY_SCRIPT_IDS: readonly string[] = ['own_code', 'sellwild_cache'];

/**
 * The id a script is counted under: its watched id, or its host for one nobody
 * named, so every outside script gets its own row with no list to keep up.
 * '' for a src that is not a URL.
 */
export function scriptIdFor(src: string): string {
  const watched = watchedScriptFor(src);
  if (watched) return watched.id;
  try {
    return new URL(src).host.toLowerCase();
    // eslint-disable-next-line sellwild-code/catch-must-log -- a src that is not a URL names no script; '' is the answer
  } catch {
    return '';
  }
}

export const SCRIPT_IDS: readonly string[] = WATCHED_SCRIPTS.map((s) => s.id);

export function watchedScriptFor(src: string): WatchedScript | undefined {
  return WATCHED_SCRIPTS.find((s) => s.match.test(src));
}

/** The friendly name, or the id itself: for a script nobody named that is its host. */
export function scriptLabel(id: string): string {
  return WATCHED_SCRIPTS.find((s) => s.id === id)?.label ?? id;
}
